import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User, Package, Clock, History, Truck, Settings, LogOut, LogIn,
  ShieldCheck, Store, Gift, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * ProfileMenu — luxury dropdown attached to the profile icon in MaisonNav.
 *
 * Design language:
 *   • Cormorant Garamond italic for the greeting (matches DIOSA wordmark)
 *   • Manrope for menu items (site sans)
 *   • #FAF6EF ivory panel with #BFA773 gold hairlines and a soft shadow
 *   • Charcoal (#1A1A18) text, gold accent on icons and section eyebrows
 *   • Subtle 8-px lift + fade-in on open, 120-ms hover slide on items
 *
 * Menu content stays the same:
 *   • Guest      → Sign in · Create account
 *   • Customer   → Orders · Track · Current · Previous · Settings · Affiliate · Logout
 *   • Admin      → same + Admin Panel · POS Terminal · Affiliate Panel
 */
export default function ProfileMenu({ testId = 'profile-menu' }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!menuRef.current || !anchorRef.current) return;
      if (menuRef.current.contains(e.target) || anchorRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'store_staff';

  const doLogout = async () => {
    try { await logout(); } catch { /* non-critical */ }
    setOpen(false);
    navigate('/');
  };

  return (
    <div className="relative" ref={anchorRef}>
      <button
        onClick={() => setOpen(v => !v)}
        aria-label={user ? 'Account' : 'Sign in'}
        className="nav-icon relative"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--charcoal, #1A1A18)',
        }}
        data-testid="nav-login-btn"
      >
        <User size={17} strokeWidth={1.5} />
      </button>

      {open && (
        <>
          <style>{`
            @keyframes diosa-profile-drop {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0);    }
            }
            .diosa-profile-menu { animation: diosa-profile-drop .18s ease-out both;
              position: absolute; right: 0; top: calc(100% + 14px);
              width: 288px; background: #FAF6EF;
              border: 1px solid #E5E0D8; border-top: 2px solid #BFA773;
              box-shadow: 0 24px 48px -20px rgba(27,24,21,0.28), 0 8px 16px -8px rgba(27,24,21,0.12);
              z-index: 600;
            }
            /* Mobile: pin below the nav, clamp to the viewport and scroll so it never gets cut off */
            @media (max-width: 640px) {
              .diosa-profile-menu {
                position: fixed;
                top: calc(var(--nav-h, 88px) + 6px);
                right: 8px; left: auto;
                width: min(300px, calc(100vw - 16px));
                max-height: calc(100vh - var(--nav-h, 88px) - 20px);
                overflow-y: auto; -webkit-overflow-scrolling: touch;
              }
            }
            .diosa-profile-item {
              display: flex; align-items: center; gap: 14px;
              padding: 11px 22px; text-decoration: none;
              color: #1A1A18; font-family: 'Manrope', sans-serif;
              font-size: 12.5px; letter-spacing: .04em;
              transition: background-color .18s ease, padding-left .18s ease, color .18s ease;
              cursor: pointer; border: none; background: transparent;
              width: 100%; text-align: left;
              position: relative;
            }
            .diosa-profile-item::before {
              content: ''; position: absolute; left: 0; top: 0; bottom: 0;
              width: 2px; background: transparent; transition: background-color .18s ease;
            }
            .diosa-profile-item:hover { background: #F3EDE2; padding-left: 28px; }
            .diosa-profile-item:hover::before { background: #BFA773; }
            .diosa-profile-item .chev {
              margin-left: auto; opacity: 0; transform: translateX(-6px);
              transition: opacity .18s ease, transform .18s ease;
            }
            .diosa-profile-item:hover .chev { opacity: .55; transform: translateX(0); }
            .diosa-profile-item.danger { color: #8A4B4B; }
            .diosa-profile-item.danger:hover::before { background: #8A4B4B; }
            .diosa-profile-eyebrow {
              padding: 12px 22px 8px;
              font-family: 'Manrope', sans-serif;
              font-size: 9.5px; letter-spacing: .28em; text-transform: uppercase;
              color: #BFA773; font-weight: 500;
            }
          `}</style>
          <div
            ref={menuRef}
            className="diosa-profile-menu"
            data-testid={testId}
          >
            {user ? (
              <>
                {/* Greeting card */}
                <div style={{ padding: '22px 22px 18px', borderBottom: '1px solid #EFE8DA' }}>
                  <p style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontStyle: 'italic', fontWeight: 400,
                    fontSize: 22, lineHeight: 1.1, color: '#1A1A18',
                    letterSpacing: '.005em',
                  }} data-testid="profile-menu-name">
                    Bonjour, {(user.name || 'friend').split(' ')[0]}
                  </p>
                  <p style={{
                    marginTop: 4,
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: 11, color: '#7A756B',
                    letterSpacing: '.02em',
                  }}>{user.email}</p>
                  {(isAdmin || isStaff) && (
                    <p style={{
                      marginTop: 10,
                      display: 'inline-block',
                      padding: '3px 9px',
                      background: '#1A1A18', color: '#FAF6EF',
                      fontSize: 9, letterSpacing: '.24em', textTransform: 'uppercase',
                      fontFamily: 'Manrope, sans-serif', fontWeight: 500,
                    }}>{isAdmin ? 'Administrator' : 'Store Staff'}</p>
                  )}
                </div>

                {/* Customer section */}
                <p className="diosa-profile-eyebrow">Your Orders</p>
                <MenuItem to="/orders"                     Icon={Package}  label="All Orders"      testId="profile-menu-orders" onNav={() => setOpen(false)} />
                <MenuItem to="/track"                Icon={Truck}    label="Track an Order"  testId="profile-menu-track-order" onNav={() => setOpen(false)} />
                <MenuItem to="/orders?status=current"      Icon={Clock}    label="Current Orders"  testId="profile-menu-current-orders" onNav={() => setOpen(false)} />
                <MenuItem to="/orders?status=previous"     Icon={History}  label="Previous Orders" testId="profile-menu-previous-orders" onNav={() => setOpen(false)} />

                <p className="diosa-profile-eyebrow">La Maison</p>
                <MenuItem to="/account?tab=settings"       Icon={Settings} label="Account Settings" testId="profile-menu-settings" onNav={() => setOpen(false)} />
                {!isAdmin && !isStaff && (
                  <MenuItem to="/affiliate"                Icon={Gift}     label="Affiliate Programme" testId="profile-menu-affiliate-apply" onNav={() => setOpen(false)} />
                )}

                {(isAdmin || isStaff) && (
                  <>
                    <p className="diosa-profile-eyebrow">Team</p>
                    {isAdmin && (
                      <MenuItem to="/admin"                Icon={ShieldCheck} label="Admin Panel"     testId="profile-menu-admin-panel" onNav={() => setOpen(false)} />
                    )}
                    <MenuItem   to="/pos"                  Icon={Store}       label="POS Terminal"    testId="profile-menu-pos" onNav={() => setOpen(false)} />
                    {isAdmin && (
                      <MenuItem to="/admin?tab=affiliates" Icon={Gift}        label="Affiliate Panel" testId="profile-menu-affiliate-panel" onNav={() => setOpen(false)} />
                    )}
                  </>
                )}

                <div style={{ borderTop: '1px solid #EFE8DA', marginTop: 6 }}>
                  <button
                    onClick={doLogout}
                    className="diosa-profile-item danger"
                    data-testid="profile-menu-logout"
                  >
                    <LogOut size={15} strokeWidth={1.5} />
                    <span>Sign out</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ padding: '22px 22px 8px' }}>
                  <p style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontStyle: 'italic', fontWeight: 400,
                    fontSize: 22, lineHeight: 1.1, color: '#1A1A18',
                  }}>Welcome to the Maison</p>
                  <p style={{
                    marginTop: 4,
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: 11, color: '#7A756B',
                    letterSpacing: '.02em',
                  }}>Sign in to view orders, save wishlists and unlock member pricing.</p>
                </div>
                <div style={{ marginTop: 6 }}>
                  <MenuItem to="/login"    Icon={LogIn} label="Sign in"        testId="profile-menu-login"    onNav={() => setOpen(false)} />
                  <MenuItem to="/register" Icon={User}  label="Create account" testId="profile-menu-register" onNav={() => setOpen(false)} />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const MenuItem = ({ to, Icon, label, testId, onNav }) => (
  <Link to={to} onClick={onNav} className="diosa-profile-item" data-testid={testId}>
    <Icon size={15} strokeWidth={1.5} style={{ color: '#BFA773' }} />
    <span>{label}</span>
    <ChevronRight size={14} strokeWidth={1.5} className="chev" />
  </Link>
);
